import { closestCenter, DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Delete from '@mui/icons-material/Delete';
import type { DashboardComponent } from 'common/types/dashboard/dashboardDefinition';
import { IconButton, Text, TextLink } from 'components/ui';
import { COMPONENT_TYPE_LABELS } from 'pages/Intelligence/SummaryDashboard/components/DashboardGrid/dashboardComponent.helpers';
import {
  DashboardComponentListMeta,
  DashboardComponentListRowBody,
} from './dashboardComponentList.styles';
import { DashboardControlStack, DashboardEmptyHint } from './dashboardControls.styles';
import DashboardSortableRow from './DashboardSortableRow';
import { useDashboardSortableSensors } from './useDashboardSortableSensors';

interface DashboardComponentListProps {
  components: readonly DashboardComponent[];
  disabled?: boolean;
  onDelete: (id: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onSelect: (id: string) => void;
  testIds?: {
    deleteButton: (id: string) => string;
    row: (id: string) => string;
  };
}

// Order here is the dashboard's real reading order: a re-flow pass reassigns every tile's x/y from
// it after each change, so dragging a row visibly moves the tile on the dashboard rather than only
// shuffling tabs.
const DashboardComponentList = ({
  components,
  disabled = false,
  onDelete,
  onReorder,
  onSelect,
  testIds,
}: DashboardComponentListProps) => {
  const sensors = useDashboardSortableSensors();
  const ids = components.map(component => component.id ?? '');

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) {
      return;
    }
    onReorder(from, to);
  };

  if (!components.length) {
    return <DashboardEmptyHint>No components yet. Add one to get started.</DashboardEmptyHint>;
  }

  return (
    <DashboardControlStack>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {components.map((component, index) => {
            const id = component.id ?? '';
            const { h = 1, w = 1 } = component.position ?? {};
            const title = component.display?.title?.trim() || COMPONENT_TYPE_LABELS[component.type];

            return (
              <DashboardSortableRow
                key={id || `component-${index}`}
                disabled={disabled || components.length < 2}
                id={id}
                action={
                  <IconButton
                    aria-label={`Delete ${title}`}
                    data-testid={testIds?.deleteButton(id)}
                    disabled={disabled}
                    onClick={() => onDelete(id)}
                    size="small">
                    <Delete fontSize="inherit" />
                  </IconButton>
                }>
                <DashboardComponentListRowBody data-testid={testIds?.row(id)}>
                  <TextLink component="button" onClick={() => onSelect(id)} type="button">
                    {title}
                  </TextLink>
                  <DashboardComponentListMeta>
                    <Text variant="caption">{COMPONENT_TYPE_LABELS[component.type]}</Text>
                    <Text variant="caption">{`${w}×${h}`}</Text>
                  </DashboardComponentListMeta>
                </DashboardComponentListRowBody>
              </DashboardSortableRow>
            );
          })}
        </SortableContext>
      </DndContext>
    </DashboardControlStack>
  );
};

export default DashboardComponentList;
